import { useEffect, useState } from 'react';
import { useTransportStore, type QuantGrid } from '@store/index';
import {
  getTransportSingletons,
  startRecording,
  stopRecording,
} from '@hooks/index';
import type { RecordMode } from '@engine/transport';
import './TransportPanel.css';

const GRID_OPTIONS: { value: QuantGrid; label: string }[] = [
  { value: 1, label: '1/4' },
  { value: 0.5, label: '1/8' },
  { value: 0.25, label: '1/16' },
  { value: 0.125, label: '1/32' },
];

const MODE_OPTIONS: { value: RecordMode; label: string; hint: string }[] = [
  { value: 'audio', label: 'Audio', hint: 'chỉ tiếng đàn' },
  { value: 'video', label: 'Video', hint: 'hình + dây, không tiếng' },
  { value: 'full', label: 'Full', hint: 'hình + tiếng' },
];

export function TransportPanel() {
  const bpm = useTransportStore((s) => s.bpm);
  const setBpm = useTransportStore((s) => s.setBpm);
  const quantum = useTransportStore((s) => s.quantum);
  const setQuantum = useTransportStore((s) => s.setQuantum);

  const metronomeOn = useTransportStore((s) => s.metronomeOn);
  const toggleMetronome = useTransportStore((s) => s.toggleMetronome);
  const metronomeVolume = useTransportStore((s) => s.metronomeVolume);
  const setMetronomeVolume = useTransportStore((s) => s.setMetronomeVolume);

  const quantizeOn = useTransportStore((s) => s.quantizeOn);
  const toggleQuantize = useTransportStore((s) => s.toggleQuantize);
  const quantizeStrength = useTransportStore((s) => s.quantizeStrength);
  const setQuantizeStrength = useTransportStore((s) => s.setQuantizeStrength);
  const quantGrid = useTransportStore((s) => s.quantGrid);
  const setQuantGrid = useTransportStore((s) => s.setQuantGrid);

  const recordMode = useTransportStore((s) => s.recordMode);
  const setRecordMode = useTransportStore((s) => s.setRecordMode);
  const recording = useTransportStore((s) => s.recording);

  const visualBeat = useTransportStore((s) => s.visualBeat);
  const visualBeatStrong = useTransportStore((s) => s.visualBeatStrong);

  const lastQuantShiftMs = useTransportStore((s) => s.lastQuantShiftMs);
  const lastQuantAtMs = useTransportStore((s) => s.lastQuantAtMs);
  const [diagFresh, setDiagFresh] = useState(false);
  useEffect(() => {
    if (!lastQuantAtMs) return;
    setDiagFresh(true);
    const id = setTimeout(() => setDiagFresh(false), 500);
    return () => clearTimeout(id);
  }, [lastQuantAtMs]);
  // Pull the live transport-singleton presence so the panel can show
  // an explicit "ready" status — if this stays "—" after Start, the
  // user knows the lazy init didn't complete (and quantize is silently
  // bypassed in the gesture loop).
  const [singletonReady, setSingletonReady] = useState(false);
  useEffect(() => {
    const check = () => {
      const t = getTransportSingletons().transport;
      setSingletonReady(t !== null);
    };
    check();
    const id = setInterval(check, 500);
    return () => clearInterval(id);
  }, []);

  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!recording) {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => {
      const rec = getTransportSingletons().recorder;
      setElapsed(rec ? rec.elapsedMs() : 0);
    }, 250);
    return () => clearInterval(id);
  }, [recording]);

  // Tap tempo — 4 taps within 2s gives a BPM by averaging the intervals.
  const [taps, setTaps] = useState<number[]>([]);
  const tap = () => {
    const now = performance.now();
    const recent = [...taps.filter((t) => now - t < 2000), now];
    setTaps(recent);
    if (recent.length >= 2) {
      const intervals: number[] = [];
      for (let i = 1; i < recent.length; i++) {
        intervals.push(recent[i]! - recent[i - 1]!);
      }
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      if (avg > 0) {
        const next = 60_000 / avg;
        setBpm(next);
      }
    }
  };

  // Pulse class — applies for ~150 ms after each beat so the indicator
  // is visible even at 60 BPM (1s gap) without latching forever.
  const beatPulseKey = useTransportStore((s) => s.visualBeatAtMs);
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (!metronomeOn) return;
    setPulsing(true);
    const id = setTimeout(() => setPulsing(false), 140);
    return () => clearTimeout(id);
  }, [beatPulseKey, metronomeOn]);

  const fmtElapsed = (ms: number) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="tp">
      <section>
        <h3 className="tp-h">Tempo</h3>
        <div className="tp-tempo">
          <button
            type="button"
            className={'tp-beat' + (pulsing && metronomeOn ? ' tp-beat--pulse' : '')
              + (visualBeatStrong ? ' tp-beat--strong' : '')}
            onClick={toggleMetronome}
            title={metronomeOn ? 'Tắt metronome' : 'Bật metronome'}
            aria-pressed={metronomeOn}
          >
            <span className="tp-beat__bpm">{bpm}</span>
            <span className="tp-beat__lbl">BPM</span>
            {metronomeOn && visualBeat >= 0 && (
              <span className="tp-beat__count">
                {((visualBeat % quantum) + quantum) % quantum + 1}/{quantum}
              </span>
            )}
          </button>
          <div className="tp-tempo__col">
            <input
              type="range"
              className="tp-range__input"
              min={40}
              max={200}
              step={1}
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
            />
            <div className="tp-row">
              <button type="button" className="tp-btn" onClick={() => setBpm(bpm - 1)}>−1</button>
              <button type="button" className="tp-btn" onClick={() => setBpm(bpm + 1)}>+1</button>
              <button type="button" className="tp-btn tp-btn--tap" onClick={tap}>TAP</button>
            </div>
          </div>
        </div>

        <div className="tp-meter">
          <span className="tp-meter__k">Time</span>
          <div className="tp-meter__nums">
            {[2, 3, 4, 5, 6, 7].map((n) => (
              <button
                key={n}
                type="button"
                className={'tp-meter__n' + (quantum === n ? ' tp-meter__n--on' : '')}
                onClick={() => setQuantum(n)}
              >
                {n}/4
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h3 className="tp-h">Metronome</h3>
        <button
          type="button"
          className={'tp-toggle' + (metronomeOn ? ' tp-toggle--on' : '')}
          onClick={toggleMetronome}
          aria-pressed={metronomeOn}
        >
          <span className="tp-toggle__dot" />
          {metronomeOn ? 'Đang chạy' : 'Tắt'}
        </button>
        <RangeRow
          label="Volume"
          value={Math.round(metronomeVolume * 100)}
          min={0}
          max={100}
          step={1}
          unit="%"
          onChange={(v) => setMetronomeVolume(v / 100)}
        />
      </section>

      <section>
        <h3 className="tp-h">Quantize</h3>
        <button
          type="button"
          className={'tp-toggle' + (quantizeOn ? ' tp-toggle--on' : '')}
          onClick={toggleQuantize}
          aria-pressed={quantizeOn}
        >
          <span className="tp-toggle__dot" />
          {quantizeOn ? `Khoá ${Math.round(quantizeStrength * 100)}%` : 'Tự do'}
        </button>
        <div className="tp-grid">
          {GRID_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={'tp-grid__n' + (quantGrid === opt.value ? ' tp-grid__n--on' : '')}
              onClick={() => setQuantGrid(opt.value)}
              disabled={!quantizeOn}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <RangeRow
          label="Strength"
          value={Math.round(quantizeStrength * 100)}
          min={0}
          max={100}
          step={1}
          unit="%"
          onChange={(v) => setQuantizeStrength(v / 100)}
          disabled={!quantizeOn}
        />
        <p className="tp-hint">
          0% = giữ nguyên cảm tay · 100% = ép vào lưới. Quanh 60–80% nghe vẫn người. Bật quantize sẽ tự bật metronome — không có click thì tai không có gì để so.
        </p>
        {quantizeOn && (
          <>
            <div className={'tp-diag' + (diagFresh ? ' tp-diag--fresh' : '')}>
              <span className="tp-diag__k">Lần pluck cuối</span>
              <span className="tp-diag__v">
                {lastQuantAtMs > 0 ? `${Math.round(lastQuantShiftMs)} ms` : '—'}
              </span>
            </div>
            <div className="tp-diag">
              <span className="tp-diag__k">Transport</span>
              <span
                className="tp-diag__v"
                style={{ color: singletonReady ? '#4ade80' : '#ff6b6b' }}
              >
                {singletonReady ? 'sẵn sàng' : 'chưa init (tap Start)'}
              </span>
            </div>
          </>
        )}
      </section>

      <section>
        <h3 className="tp-h">Record</h3>
        <div className="tp-modes">
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={'tp-mode' + (recordMode === opt.value ? ' tp-mode--on' : '')}
              onClick={() => setRecordMode(opt.value)}
              disabled={recording}
            >
              <span className="tp-mode__label">{opt.label}</span>
              <span className="tp-mode__hint">{opt.hint}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={'tp-rec' + (recording ? ' tp-rec--on' : '')}
          onClick={() => (recording ? stopRecording() : startRecording())}
        >
          <span className="tp-rec__dot" />
          {recording ? `Đang ghi · ${fmtElapsed(elapsed)}` : `Bắt đầu ghi ${recordMode}`}
        </button>
        {recording && (
          <p className="tp-hint">File sẽ tự tải về khi bạn dừng.</p>
        )}
      </section>
    </div>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className={'tp-range' + (disabled ? ' tp-range--off' : '')}>
      <div className="tp-range__head">
        <span className="tp-range__k">{label}</span>
        <span className="tp-range__v">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        className="tp-range__input"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          background: `linear-gradient(90deg, var(--key-root) ${pct}%, rgba(255,255,255,0.1) ${pct}%)`,
        }}
      />
    </div>
  );
}
